import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faGlobe } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";

const NewsList = () => {
    const [newsletters, setNewsletters] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const employeeCode = storageService.getItem("employeeCode");
    const roleId = Number(storageService.getItem("roleId"));

    const fetchNewsletters = async () => {
        setLoading(true);
        try {
            let currentSiteId = null;
            if (employeeCode) {
                const userRes = await apiServices({
                    url: '/ResidentialUser/GetResidentialUserById',
                    method: 'POST',
                    data: { userId: employeeCode }
                });
                if (userRes.success && userRes.data?.data?.[0]) {
                    currentSiteId = userRes.data.data[0].siteId;
                }
            }

            const res = await apiServices({
                url: '/ResidentialMarketingNews/GetAllMarketingNews',
                method: 'GET'
            });

            if (res.success && res.data?.Marketingnews) {
                let mapped = res.data.Marketingnews.map((n, i) => ({
                    id: n.Id,
                    title: n.Title || '',
                    subTitle: n.SubTitle || '',
                    status: n.IsActive === 'Active' ? 'Active' : 'Inactive',
                    siteId: n.SiteId,
                    unitId: n.UnitId,
                    image: n.Image,
                    backgroundColor: n.BackgroundColor,
                    textColor: n.TextColor,
                    navigationLink: n.NavigationLink,
                    originalIndex: i
                }));

                if (roleId === 3 && currentSiteId) {
                    mapped = mapped.filter(n => n.siteId === currentSiteId);
                }

                mapped.sort((a, b) => b.id - a.id);
                setNewsletters(mapped);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: mapped.length,
                    totalPages: Math.ceil(mapped.length / prev.pageSize)
                }));
            } else {
                setNewsletters([]);
            }
        } catch (error) {
            console.error("Error fetching newsletters:", error);
            toast.error("Failed to fetch newsletters");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNewsletters();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Are you sure you want to delete this newsletter?")) return;
        try {
            // Adjust API endpoint based on your actual delete route
            const res = await apiServices({
                url: `/ResidentialMarketingNews/deleteMarketingNews/${row.id}`,
                method: 'DELETE'
            });
            if (res.success || res.isSuccess || res.IsSuccess || res) {
                toast.success("Newsletter deleted successfully");
                fetchNewsletters();
            } else {
                toast.error("Failed to delete newsletter");
            }
        } catch (error) {
            console.error("Error deleting newsletter:", error);
            toast.error("An error occurred while deleting");
        }
    };

    const handleEdit = (row) => {
        navigate(`/zentres/News-Letters/edit/${row.id}`);
    };

    const columns = [
        { key: "no", label: "No", align: "left" },
        { key: "title", label: "Title", align: "left" },
        { key: "subTitle", label: "Sub Title", align: "left" },
        { key: "status", label: "Status", align: "center" },
        { key: "actions", label: "Actions", align: "center" }
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "title":
                return <span className="text-[13px] font-bold text-gray-800 tracking-tight">{row.title || "—"}</span>;
            case "subTitle":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.subTitle || "—"}</span>;
            case "status":
                const status = row.status || 'Inactive';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-[13px] font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const expandedRowRender = (row) => {
        return (
            <div className="p-6 bg-gray-50 flex items-start gap-8">
                <div 
                    className="w-48 h-32 rounded-lg overflow-hidden border shadow-sm shrink-0 flex items-center justify-center relative"
                    style={{ backgroundColor: row.backgroundColor || '#ffffff' }}
                >
                    {row.image && (
                        <img 
                            src={row.image} 
                            alt={row.title} 
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    )}
                </div>
                <div className="flex flex-col gap-3">
                    <h4 className="text-sm font-bold text-gray-800">Newsletter Details</h4>
                    <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-[13px]">
                        <div className="flex flex-col">
                            <span className="text-gray-500 font-medium">Background Color</span>
                            <div className="flex items-center gap-2 mt-1">
                                <span 
                                    className="w-4 h-4 rounded-full border shadow-sm" 
                                    style={{ backgroundColor: row.backgroundColor || '#ffffff' }}
                                ></span>
                                <span className="font-semibold text-gray-700">{row.backgroundColor || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-gray-500 font-medium">Text Color</span>
                            <div className="flex items-center gap-2 mt-1">
                                <span 
                                    className="w-4 h-4 rounded-full border shadow-sm" 
                                    style={{ backgroundColor: row.textColor || '#000000' }}
                                ></span>
                                <span className="font-semibold text-gray-700">{row.textColor || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="flex flex-col col-span-2 mt-2">
                            <span className="text-gray-500 font-medium">Navigation Link</span>
                            <span className="font-semibold text-gray-700 break-all">{row.navigationLink || 'No link provided'}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const filteredData = useMemo(() => {
        let data = newsletters;
        if (searchTerm) {
            data = data.filter(item => 
                (item.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.subTitle || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (statusFilter) {
            data = data.filter(item => item.status === statusFilter);
        }
        return data;
    }, [newsletters, searchTerm, statusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-60">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                    type="text"
                    placeholder="Search Newsletters..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
            </div>
            
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="w-full h-full flex flex-col gap-4">
            <div className="shrink-0 flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CommonTable
                    headers={columns}
                    headerAction={headerAction}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    expandedRowRender={expandedRowRender}
                />
            </div>
        </div>
    );
};

export default NewsList;
